import { useEffect, useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

// Enterprise "Contact Sales": a short form that lands in the database
// (submit_sales_inquiry, migration 032) and on Admin → Feedback.

const TEAM_SIZES = ["Just me", "2–5", "6–20", "21–100", "100+"];
const VOLUMES = ["Under 50,000", "50,000–100,000", "100,000–500,000", "500,000+", "Not sure yet"];

const Field = ({ id, label, children }) => (
  <div className="space-y-1.5">
    <label htmlFor={id} className="text-[13px] font-medium">{label}</label>
    {children}
  </div>
);

function Choice({ id, options, value, onChange }) {
  return (
    <div role="radiogroup" aria-labelledby={id} className="flex flex-wrap gap-1.5">
      {options.map((o) => (
        <button key={o} type="button" role="radio" aria-checked={value === o} onClick={() => onChange(o)}
          className={`rounded-full border px-3 py-1 text-sm transition-colors ${
            value === o ? "border-primary bg-violet-soft text-violet-text" : "border-border hover:bg-surface-hover"}`}>
          {o}
        </button>
      ))}
    </div>
  );
}

export default function ContactSalesDialog({ open, onClose }) {
  const { user } = useAuth();
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (open) {
      setSent(false);
      setF({ name: user?.name || "", email: user?.email || "", company: user?.company || "", phone: "", team: "", volume: "", needs: "" });
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (v) => setF((p) => ({ ...p, [k]: typeof v === "string" ? v : v.target.value }));
  const ready = f.name?.trim() && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email || "") && f.company?.trim() && f.needs?.trim();

  const submit = async (e) => {
    e.preventDefault();
    if (!ready) return;
    setBusy(true);
    const { data, error } = await supabase.rpc("submit_sales_inquiry", {
      p_name: f.name.trim(), p_email: f.email.trim(), p_company: f.company.trim(),
      p_phone: f.phone.trim() || null, p_team_size: f.team || null,
      p_monthly_volume: f.volume || null, p_needs: f.needs.trim(),
    });
    setBusy(false);
    if (error || data?.error) { toast.error("Couldn't send that. Please try again."); return; }
    setSent(true);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="contact-sales-dialog">
        {sent ? (
          <div className="space-y-4 text-center" data-testid="contact-sales-sent">
            <span className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-success-soft text-success">
              <CheckCircle2 className="h-6 w-6" />
            </span>
            <DialogHeader className="text-center sm:text-center">
              <DialogTitle className="font-display text-2xl">Thanks, we'll be in touch</DialogTitle>
              <DialogDescription>We will reply to {f.email}.</DialogDescription>
            </DialogHeader>
            <Button onClick={onClose}>Done</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Talk to sales</DialogTitle>
              <DialogDescription>Tell us what you need and we'll put together an Enterprise plan.</DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field id="cs-name" label="Your name"><Input id="cs-name" value={f.name} onChange={set("name")} data-testid="cs-name" /></Field>
              <Field id="cs-email" label="Work email"><Input id="cs-email" type="email" value={f.email} onChange={set("email")} data-testid="cs-email" /></Field>
              <Field id="cs-company" label="Company"><Input id="cs-company" value={f.company} onChange={set("company")} data-testid="cs-company" /></Field>
              <Field id="cs-phone" label="Phone (optional)"><Input id="cs-phone" type="tel" value={f.phone} onChange={set("phone")} data-testid="cs-phone" /></Field>
            </div>
            <div className="space-y-1.5">
              <div id="cs-team" className="text-[13px] font-medium">Team size</div>
              <Choice id="cs-team" options={TEAM_SIZES} value={f.team} onChange={set("team")} />
            </div>
            <div className="space-y-1.5">
              <div id="cs-volume" className="text-[13px] font-medium">Credits you'd need a month</div>
              <Choice id="cs-volume" options={VOLUMES} value={f.volume} onChange={set("volume")} />
            </div>
            <Field id="cs-needs" label="What are you looking for?">
              <Textarea id="cs-needs" rows={4} value={f.needs} onChange={set("needs")} data-testid="cs-needs"
                placeholder="Which markets or roles, CRM integration, invoicing, data you need…" />
            </Field>
            <DialogFooter className="gap-2 sm:gap-2">
              <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
              <Button type="submit" disabled={!ready || busy} data-testid="cs-submit">Send request</Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
